import { Component, inject, OnInit } from '@angular/core';
import { MenuComponentBase } from '../../../../shared/base-classes/menu-component-base';
import { LeadsCriteriaService } from '../../services/leads-criteria.service';
import { LeadsCriteria } from '../../models/leads-criteria.model';
import { Subscription, takeUntil, timer } from 'rxjs';
import { SvgIconComponent } from '../../../../shared/components/svg-icon/svg-icon.component';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Assignee } from '../../../../shared/interfaces/company';
import { DatePipe } from '@angular/common';
import { PopupComponent } from '../../../../shared/components/popup/popup.component';
import { AuthService } from '../../../auth/services/auth.service';
import { SearchInputComponent } from '../../../../shared/components/search-input/search-input.component';
import { AuditLogService } from '../../../../shared/services/audit-log/audit-log.service';

@Component({
  selector: 'app-leads-criteria',
  standalone: true,
  imports: [
    SearchInputComponent,
    SvgIconComponent,
    FormsModule,
    DatePipe,
    PopupComponent,
  ],
  templateUrl: './leads-criteria.component.html',
  styleUrl: './leads-criteria.component.scss',
})
export class LeadsCriteriaComponent
  extends MenuComponentBase
  implements OnInit
{
  leadsCriteriaService = inject(LeadsCriteriaService);
  router = inject(Router);
  authService = inject(AuthService);
  auditLogService = inject(AuditLogService);

  leadsCriteria: LeadsCriteria[] = [];
  searchQuery = '';
  showDeleteConfirmation = false;
  criteriaToDelete: LeadsCriteria | null = null;
  loading = false;
  private pollingSubscription?: Subscription;

  get filteredLeadsCriteria(): LeadsCriteria[] {
    if (!this.searchQuery) {
      return this.leadsCriteria;
    }

    const query = this.searchQuery.toLowerCase();
    return this.leadsCriteria.filter(
      (criteria) =>
        criteria.name.toLowerCase().includes(query) ||
        (criteria.description &&
          criteria.description.toLowerCase().includes(query)) ||
        (criteria.created_by?.first_name &&
          criteria.created_by.first_name.toLowerCase().includes(query)) ||
        (criteria.created_by?.last_name &&
          criteria.created_by.last_name.toLowerCase().includes(query)) ||
        (criteria.created_by?.username &&
          criteria.created_by.username.toLowerCase().includes(query)),
    );
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.loadLeadsCriteria();
  }

  loadLeadsCriteria(searchQuery?: string, isPolling = false): void {
    if (!isPolling) {
      this.loading = true;
    }

    this.leadsCriteriaService
      .getLeadsCriteria(searchQuery, undefined, isPolling)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (criteria) => {
          this.leadsCriteria = criteria;
          this.loading = false;
          this.checkAndManagePolling();
        },
        error: (error) => {
          console.error('Error fetching leads criteria:', error);
          this.loading = false;
          this.stopPolling();
        },
      });
  }

  private checkAndManagePolling(): void {
    const hasMatchingInProgress = this.leadsCriteria.some(
      (c) => c.is_matching_in_progress,
    );

    if (hasMatchingInProgress) {
      if (!this.pollingSubscription || this.pollingSubscription.closed) {
        this.pollingSubscription = timer(10000, 10000)
          .pipe(takeUntil(this.destroy$))
          .subscribe(() => {
            this.loadLeadsCriteria(this.searchQuery, true);
          });
      }
    } else {
      this.stopPolling();
    }
  }

  private stopPolling(): void {
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
      this.pollingSubscription = undefined;
    }
  }

  onAddCriteria(): void {
    this.router.navigate(['create'], { relativeTo: this.route });
  }

  onViewDetails(id: number): void {
    this.router.navigate(['details', id], { relativeTo: this.route });
  }

  getCreatedByName(assignee: Assignee): string {
    if (!assignee) return 'Unknown';

    if (assignee.first_name && assignee.last_name) {
      return `${assignee.first_name} ${assignee.last_name}`;
    } else if (assignee.first_name) {
      return assignee.first_name;
    } else if (assignee.last_name) {
      return assignee.last_name;
    } else if (assignee.username) {
      return assignee.username;
    }

    return 'Unknown';
  }

  onToggleActiveStatus(event: Event, criteria: LeadsCriteria): void {
    event.stopPropagation(); // Prevent card click event

    this.auditLogService.logFeature({
      featureName: 'leads_criteria',
      action: criteria.is_active ? 'disable' : 'enable',
      meta: {
        criteriaId: String(criteria.id),
        criteriaName: criteria.name,
      },
      description: criteria.is_active
        ? 'User disables leads criteria'
        : 'User enables leads criteria',
    });

    const updatedCriteria: Partial<LeadsCriteria> = {
      is_active: !criteria.is_active,
    };

    this.leadsCriteriaService
      .updateLeadCriteria(
        criteria.id.toString(),
        updatedCriteria as LeadsCriteria,
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (updated) => {
          // Update the local array
          const index = this.leadsCriteria.findIndex(
            (c) => c.id === criteria.id,
          );
          if (index !== -1) {
            const loggedInUserInfo = this.authService.getLoggedInUserInfo();
            const assignedUserInfo = new Assignee();
            assignedUserInfo.username = loggedInUserInfo?.username;
            assignedUserInfo.first_name = loggedInUserInfo?.name?.split(' ')[0];
            assignedUserInfo.group_name = loggedInUserInfo?.user_group;
            updated.updated_by = assignedUserInfo;
            updated.updated_at_utc = new Date();
            if (updated.is_active) {
              updated.is_matching_in_progress = true;
            }
            this.leadsCriteria[index] = updated;
            if (updated.is_active) {
              this.checkAndManagePolling();
            }
          }
        },
        error: (error) => {
          console.error('Error updating status:', error);
          // You could add a toast notification here
        },
      });
  }

  onDeleteCriteria(event: Event, criteria: LeadsCriteria): void {
    event.stopPropagation(); // Prevent card click event

    // Show custom confirmation popup
    this.criteriaToDelete = criteria;
    this.showDeleteConfirmation = true;
  }

  onConfirmDelete(): void {
    if (!this.criteriaToDelete) return;

    this.auditLogService.logFeature({
      featureName: 'leads_criteria',
      action: 'delete',
      meta: {
        criteriaId: String(this.criteriaToDelete.id),
        criteriaName: this.criteriaToDelete.name,
      },
      description: 'User deletes leads criteria',
    });

    this.leadsCriteriaService
      .deleteLeadCriteria(this.criteriaToDelete.id.toString())
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          // Remove from local array
          this.leadsCriteria = this.leadsCriteria.filter(
            (c) => c.id !== this.criteriaToDelete!.id,
          );
          this.onCancelDelete();
        },
        error: (error) => {
          console.error('Error deleting lead criteria:', error);
          this.onCancelDelete();
          // You could add a toast notification here
        },
      });
  }

  onCancelDelete(): void {
    this.showDeleteConfirmation = false;
    this.criteriaToDelete = null;
  }

  onSearchQueryChange(query: string): void {
    this.searchQuery = query;
    this.loadLeadsCriteria(this.searchQuery);
  }
}
